"use client";

import React from "react";
import { Check, Database, Shield, Users, Zap, Layers3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const capacityPacks = [{
  id: "pack-small",
  name: "Small Pack",
  price: "$199",
  description: "Ideal for smaller expansions when one resource starts to tighten.",
  popular: false,
  options: [{
    id: "s-queries",
    label: "+50,000 Queries / Month",
    icon: Zap
  }, {
    id: "s-storage",
    label: "+10GB Indexed Storage",
    icon: Database
  }, {
    id: "s-tenants",
    label: "+1M Vectors",
    icon: Users
  }]
}, {
  id: "pack-medium",
  name: "Medium Pack",
  price: "$499",
  description: "The standard increment for growing teams, agencies, and tenant environments.",
  popular: true,
  options: [{
    id: "m-queries",
    label: "+150,000 Queries / Month",
    icon: Zap
  }, {
    id: "m-storage",
    label: "+30GB Indexed Storage",
    icon: Database
  }, {
    id: "m-tenants",
    label: "+3M Vectors",
    icon: Users
  }]
}, {
  id: "pack-large",
  name: "Large Pack",
  price: "$999",
  description: "Larger-scale expansion for higher-volume delivery and busier environments.",
  popular: false,
  options: [{
    id: "l-queries",
    label: "+400,000 Queries / Month",
    icon: Zap
  }, {
    id: "l-storage",
    label: "+80GB Indexed Storage",
    icon: Database
  }, {
    id: "l-tenants",
    label: "+8M Vectors",
    icon: Users
  }]
}];
export default function WhatCapacityPacksCover() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="what-capacity-packs-cover" className="bg-background py-16 sm:py-20 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        <div className="mx-auto max-w-3xl text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Shield className="size-4" />
            <span>Predictable Scaling</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
            Capacity Packs expand one metric at a time
          </h2>
          <p className="text-lg text-muted-foreground">
            EloRAG keeps scaling predictable by letting you expand the specific resource that needs it,
            instead of forcing a full plan change every time one limit gets tight.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {capacityPacks.map((pack, i) => <div data-index={i} key={pack.id} className="flex">
              <Card className={`relative flex flex-col w-full transition-all duration-300 hover:shadow-lg ${pack.popular ? "border-primary shadow-md ring-1 ring-primary/20" : "border-border shadow-sm"}`}>
                {pack.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full shadow-sm">
                    Most Popular
                  </div>}
                <CardHeader className="pb-6">
                  <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                    <Layers3 className="size-3.5" />
                    Monthly add-on
                  </div>

                  <CardTitle className="text-xl">{pack.name}</CardTitle>
                  <div className="mt-4 flex items-baseline text-4xl font-bold text-foreground">
                    {pack.price}
                    <span className="ml-1 text-sm font-medium text-muted-foreground">
                      /mo
                    </span>
                  </div>
                  <CardDescription className="mt-4 text-sm leading-relaxed">
                    {pack.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 pb-6">
                  <div className="mb-4 text-sm font-medium text-foreground">
                    Select how to apply this pack:
                  </div>
                  <RadioGroup defaultValue={pack.options[0].id} className="gap-3">
                    {pack.options.map((option, j) => <label key={option.id} data-index={j} htmlFor={option.id} className="group relative flex cursor-pointer rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:bg-accent/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 has-[[data-state=checked]]:ring-1 has-[[data-state=checked]]:ring-primary">
                        <div className="flex w-full items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background border border-border group-has-[[data-state=checked]]:bg-primary group-has-[[data-state=checked]]:border-primary group-has-[[data-state=checked]]:text-primary-foreground text-muted-foreground transition-colors">
                              <option.icon className="size-4" />
                            </div>
                            <span className="text-sm font-medium text-foreground">
                              {option.label}
                            </span>
                          </div>
                          <RadioGroupItem value={option.id} id={option.id} className="sr-only" />
                          <div className="size-4 rounded-full border border-primary/30 flex items-center justify-center group-has-[[data-state=checked]]:border-primary group-has-[[data-state=checked]]:bg-primary transition-colors">
                            <Check className="size-2.5 text-primary-foreground opacity-0 group-has-[[data-state=checked]]:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </label>)}
                  </RadioGroup>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button variant={pack.popular ? "default" : "outline"} className="w-full">
                    Add {pack.name}
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            AI usage costs are not included in Capacity Packs. EloRAG uses a BYO-Key model, so you only pay your AI providers for the usage you actually generate.
          </p>
        </div>
      </div>
    </section>;
}